import * as React from "react";
import { motion, useReducedMotion } from "framer-motion";
import confetti from "canvas-confetti";
import { Dialog, DialogContent, DialogDescription, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { AchievementBadge, TIER_STYLE, TierLabel, tierOf } from "./AchievementBadge";

export interface UnlockedAchievement {
  id: string;
  name: string;
  description?: string;
  tier?: string;
  icon?: string | null;
  xpReward: number;
}

interface Props {
  achievement: UnlockedAchievement | null;
  /** Position in the queue, for "1 of 3". */
  index: number;
  total: number;
  onNext: () => void;
  onViewAll: () => void;
}

/**
 * The moment a badge is earned. One dialog, one badge at a time; a queue of
 * several steps through with "Next". Motion is a single scale-in with a slow
 * halo; confetti is reserved for gold and legendary and skipped entirely for
 * reduced-motion users.
 */
export function AchievementCelebration({ achievement, index, total, onNext, onViewAll }: Props) {
  const reduceMotion = useReducedMotion();
  const tier = tierOf(achievement?.tier);
  const t = TIER_STYLE[tier];

  React.useEffect(() => {
    if (!achievement || reduceMotion) return;
    if (tier !== "gold" && tier !== "legendary") return;
    const colors = [t.rim[1], t.face[0], t.face[1]];
    const fire = (x: number) =>
      confetti({ particleCount: tier === "legendary" ? 70 : 45, spread: 70, startVelocity: 38, origin: { x, y: 0.62 }, colors, zIndex: 9999, disableForReducedMotion: true });
    const a = window.setTimeout(() => fire(0.3), 180);
    const b = window.setTimeout(() => fire(0.7), 320);
    return () => {
      window.clearTimeout(a);
      window.clearTimeout(b);
    };
  }, [achievement, reduceMotion, tier, t]);

  const isLast = index >= total - 1;

  return (
    <Dialog open={!!achievement} onOpenChange={(open) => { if (!open) onNext(); }}>
      <DialogContent data-achievement-dialog className="overflow-hidden p-0 sm:max-w-sm" mobileSheet={false}>
        {achievement && (
          <div className="flex flex-col items-center px-6 pb-6 pt-8 text-center">
            <div className="relative mb-5 grid place-items-center" style={{ width: 176, height: 176 }}>
              {/* Halo */}
              <motion.div
                aria-hidden="true"
                className="absolute inset-0 rounded-full"
                style={{
                  background: `conic-gradient(from 0deg, transparent, ${t.glow}, transparent 30%, ${t.glow}, transparent 60%, ${t.glow}, transparent)`,
                  filter: "blur(10px)",
                }}
                animate={reduceMotion ? undefined : { rotate: 360 }}
                transition={{ duration: 14, ease: "linear", repeat: Infinity }}
              />
              <div aria-hidden="true" className="absolute inset-6 rounded-full" style={{ background: `radial-gradient(circle, ${t.glow}, transparent 70%)` }} />
              <motion.div
                key={achievement.id}
                initial={reduceMotion ? { opacity: 0 } : { opacity: 0, scale: 0.55, rotate: -12 }}
                animate={reduceMotion ? { opacity: 1 } : { opacity: 1, scale: 1, rotate: 0 }}
                transition={{ type: "spring", stiffness: 260, damping: 18 }}
              >
                <AchievementBadge icon={achievement.icon} tier={achievement.tier} size="xl" />
              </motion.div>
            </div>

            <p className="text-label text-ember">{total > 1 ? `Badge unlocked · ${index + 1} of ${total}` : "Badge unlocked"}</p>
            <DialogTitle className="display-font mt-1.5 text-[1.75rem] font-semibold leading-tight">{achievement.name}</DialogTitle>
            {achievement.description && (
              <DialogDescription className="mt-2 max-w-[18rem] text-body-sm text-ink-2">{achievement.description}</DialogDescription>
            )}

            <div className="mt-4 flex items-center gap-2">
              <TierLabel tier={achievement.tier} />
              {achievement.xpReward > 0 && (
                <span className="rounded-full bg-ember-soft px-2 py-0.5 text-caption font-semibold text-ember-soft-ink tabular-nums">
                  +{achievement.xpReward} XP
                </span>
              )}
            </div>

            <div className="mt-6 flex w-full flex-col gap-2">
              <Button size="lg" onClick={onNext} autoFocus>
                {isLast ? "Nice!" : "Next badge"}
              </Button>
              <Button variant="ghost" onClick={onViewAll}>
                See all badges
              </Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

/** More than this many unlocks at once get one summary instead of a step-through. */
export const SUMMARY_THRESHOLD = 3;
const SUMMARY_GRID_MAX = 9;

interface SummaryProps {
  achievements: UnlockedAchievement[] | null;
  onClose: () => void;
  onViewAll: () => void;
}

/**
 * Several badges at once (a first login after a busy week, or a backfill)
 * would be a chore to click through one by one, so they're shown together:
 * a grid of medallions, the XP they add up to, and one button to move on.
 */
export function AchievementSummary({ achievements, onClose, onViewAll }: SummaryProps) {
  const reduceMotion = useReducedMotion();
  const list = achievements ?? [];
  const shown = list.slice(0, SUMMARY_GRID_MAX);
  const extra = list.length - shown.length;
  const totalXp = list.reduce((sum, a) => sum + (a.xpReward || 0), 0);
  const best = list.some((a) => tierOf(a.tier) === "legendary") ? "legendary" : list.some((a) => tierOf(a.tier) === "gold") ? "gold" : null;

  React.useEffect(() => {
    if (!achievements || reduceMotion || !best) return;
    const t = TIER_STYLE[best];
    const id = window.setTimeout(
      () => confetti({ particleCount: 60, spread: 80, startVelocity: 36, origin: { x: 0.5, y: 0.55 }, colors: [t.rim[1], t.face[0], t.face[1]], zIndex: 9999, disableForReducedMotion: true }),
      200,
    );
    return () => window.clearTimeout(id);
  }, [achievements, reduceMotion, best]);

  return (
    <Dialog open={!!achievements} onOpenChange={(open) => { if (!open) onClose(); }}>
      <DialogContent data-achievement-dialog className="p-0 sm:max-w-md" mobileSheet={false}>
        {achievements && (
          <div className="flex flex-col items-center px-6 pb-6 pt-7 text-center">
            <p className="text-label text-ember">Badges unlocked</p>
            <DialogTitle className="display-font mt-1.5 text-[1.75rem] font-semibold leading-tight">
              You earned {list.length} badges
            </DialogTitle>
            <DialogDescription className="mt-2 text-body-sm text-ink-2">
              {totalXp > 0 ? (
                <>Together they're worth <span className="font-semibold text-ink tabular-nums">+{totalXp.toLocaleString()} XP</span>.</>
              ) : (
                "Nice work. Here's everything you picked up."
              )}
            </DialogDescription>

            <ul className="mt-6 grid w-full grid-cols-3 gap-x-2 gap-y-4" aria-label="Badges earned">
              {shown.map((a, i) => (
                <motion.li
                  key={a.id}
                  className="flex min-w-0 flex-col items-center gap-1.5"
                  initial={reduceMotion ? { opacity: 0 } : { opacity: 0, scale: 0.6 }}
                  animate={reduceMotion ? { opacity: 1 } : { opacity: 1, scale: 1 }}
                  transition={reduceMotion ? { duration: 0.15 } : { type: "spring", stiffness: 280, damping: 20, delay: 0.05 * i }}
                >
                  <AchievementBadge icon={a.icon} tier={a.tier} size="md" />
                  <span className="line-clamp-2 text-caption font-medium text-ink">{a.name}</span>
                </motion.li>
              ))}
            </ul>
            {extra > 0 && <p className="mt-3 text-body-sm text-ink-3">+{extra} more</p>}

            <div className="mt-6 flex w-full flex-col gap-2">
              <Button size="lg" onClick={onClose} autoFocus>
                Nice!
              </Button>
              <Button variant="ghost" onClick={onViewAll}>
                See all badges
              </Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

export default AchievementCelebration;
